'use client';

import { Suspense, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { TakeoverProvider } from '@takeover/sdk/client';
import { PrivyClientProvider } from './privy-provider';
import { PrivyBootErrorBoundary } from './privy-boot-error-boundary';
import { QueryProvider } from './query-provider';
import { RateLimitProvider } from './rate-limit-provider';
import { TransactionQueueProvider } from '@/hooks/use-transactions';
import { TransactionSnackbar } from '@/components/transactions/transaction-snackbar';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ProfileModalProvider } from '@/contexts/profile-modal-context';
import { ProfileModalHost } from '@/components/profile/profile-modal-host';
import { LeaderboardModalProvider } from '@/contexts/leaderboard-modal-context';
import { MobileChromeProvider } from '@/contexts/mobile-chrome-context';
import { ChatDraftProvider } from '@/contexts/chat-draft-context';
import { getTakeoverSDKConfig } from '@/lib/takeover-sdk';
import { WalletDiagnosticSnapshot } from '@/components/debug/wallet-diagnostic-snapshot';
import { recordBootPhase } from '@/lib/boot-milestones';

/**
 * Core infrastructure providers every route depends on.
 *
 * Splitting these out from the UI/portal providers lets us reason
 * about the tree in two layers:
 *   - `CoreProviders` (below) — auth, data, transactions. Mandatory
 *     for any route that talks to the backend or a wallet.
 *   - `UIShellProviders` — modal/tooltip contexts + their portal
 *     hosts (including `ProfileModalHost`, the home of the in-app
 *     portfolio + profile view). Consumed by `Header`, `Footer`,
 *     `ActivityTicker`, lobby, every coin/board route, etc. Still
 *     global today because the app chrome depends on them on
 *     non-chromeless routes; keeping them as a named component makes
 *     it trivial to move them into a route-scoped layout later if we
 *     decide to peel off e.g. `/landing`.
 *
 * `DemoActivityProvider` is notably absent: it used to live at the
 * root, but its only consumer (`activity-ticker`) is null-safe via
 * `useDemoActivityOptional`, so we now mount it only on the coin demo
 * route the gate matches in `AppShell`. Saves a little render work on
 * every other page. (The standalone `/coin/[addr]/demo` page itself
 * has been retired; the gate stays in case a similar demo surface is
 * reintroduced under the same route shape.)
 */
function ProvidersBootRecorder() {
  const pathname = usePathname();
  useEffect(() => {
    recordBootPhase('routes.core_providers.mount', {
      pathname,
      href:
        typeof window !== 'undefined' ? window.location.href : null,
    });
  }, [pathname]);
  return null;
}

function CoreProviders({ children }: { children: React.ReactNode }) {
  const sdkConfig = getTakeoverSDKConfig();
  return (
    <TakeoverProvider config={sdkConfig}>
      <ProvidersBootRecorder />
      <RateLimitProvider>
        <QueryProvider>
          <PrivyBootErrorBoundary>
            <PrivyClientProvider>
            {/* Headless observer for Privy + wagmi state. Renders
                nothing; writes a snapshot to the diagnostic-state
                singleton on every meaningful wallet state change so
                the next client-error report (sent by `<ErrorTrap />`
                or the boundary `useEffect`s) carries the *last
                known* wallet state plus a chronological ring of
                wallet transition events. Mounted as a sibling of
                `<TransactionQueueProvider>` so it doesn't depend on
                it — if the transaction-queue tree throws, the
                observer keeps updating the snapshot. Sits *inside*
                `<PrivyClientProvider>` because it consumes Privy /
                wagmi hooks. */}
              <WalletDiagnosticSnapshot />
              <TransactionQueueProvider>
                {children}
                <TransactionSnackbar />
              {/* No global Sonner toaster: top-center toasts were
                  removed by design. Existing `toast(...)` call sites
                  in the codebase silently no-op without a portal —
                  do not re-add `<Toaster />` here. Surface user
                  feedback through `TransactionSnackbar`, in-drawer
                  inline errors, or the notifications bell instead. */}
              </TransactionQueueProvider>
            </PrivyClientProvider>
          </PrivyBootErrorBoundary>
        </QueryProvider>
      </RateLimitProvider>
    </TakeoverProvider>
  );
}

/**
 * Modal / preview contexts + their portal hosts.
 *
 * Mounted globally because the app chrome (Header, Footer, MobileNav,
 * ActivityTicker) consumes `useProfileModal` and `useLeaderboardModal`
 * on every non-chromeless route. The portal host `ProfileModalHost`
 * renders nothing when its state is closed — idle cost is a single
 * zero-prop component — so keeping it mounted is cheap.
 */
function UIShellProviders({ children }: { children: React.ReactNode }) {
  // `TooltipProvider` has to wrap everything modal-related (not the other way
  // round) because `LeaderboardModalProvider` mounts `<LeaderboardModal>` as
  // a *sibling* of `children` — if the tooltip provider lives inside it, the
  // modal itself renders outside the provider and any `<Tooltip>` inside
  // (e.g. avatar tooltips on leaderboard rows) crashes with
  // "Tooltip must be used within TooltipProvider". Same reasoning covers
  // `ProfileModalHost` which portals its own DOM.
  //
  // `LeaderboardModalProvider` calls `useSearchParams()` to pop the modal
  // when the retired `/leaderboard` route lands the user back at `/?lb=1`.
  // Next.js 14's static prerender bails the whole subtree when a hook like
  // that runs without a Suspense boundary above it (the dreaded
  // "useSearchParams() should be wrapped in a suspense boundary" CSR
  // bailout). Wrapping the provider — not just the modal — keeps the entire
  // route tree behind a single Suspense fence so every page (`/`, `/chat`,
  // `/admin`, the dev surfaces) can prerender cleanly. Fallback is `null`
  // because the provider renders no chrome of its own; only the deferred
  // `useEffect` consumer actually needs the URL.
  // `MobileChromeProvider` carries the chip-rail registration for the
  // mobile shell (see `useChipRail` in `mobile-chrome-context`). It sits
  // here so both `AppShell` (which mounts the chip-rail host inside the
  // fixed top chrome) and route consumers (`LobbyClient`, `/chat`) can
  // share state without prop-drilling. `ChatDraftProvider` keeps an
  // in-flight chat composition alive across sheet/route unmounts —
  // mounted at the same level so a draft survives navigation between
  // any two surfaces that mount the chat input.
  return (
    <TooltipProvider delayDuration={100}>
      <MobileChromeProvider>
        <ChatDraftProvider>
          <ProfileModalProvider>
            <Suspense fallback={null}>
              <LeaderboardModalProvider>
                {children}
                <ProfileModalHost />
              </LeaderboardModalProvider>
            </Suspense>
          </ProfileModalProvider>
        </ChatDraftProvider>
      </MobileChromeProvider>
    </TooltipProvider>
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  // `/debug` is the rescue surface: it has to work even when the
  // providers tree below would crash on mount. A malformed wallet
  // extension refusing `Object.defineProperty(window, 'ethereum')`,
  // a Privy auth cookie corrupted in a way that loops the SDK, a
  // QueryProvider failure — every real-world "my page doesn't load"
  // report we've handled lives inside this subtree. Short-circuiting
  // providers for exactly one pathname means support can DM
  // `takeover.fun/debug` to a stuck user and know it will paint.
  //
  // Nothing else changes: `ErrorTrap` is mounted outside `<Providers>`
  // in `app/layout.tsx` so the trap still captures errors on the
  // debug page; the route bypasses chrome separately via AppShell's
  // `CHROMELESS_ROUTES` list. Pathname-driven provider gating is
  // safe under React's render model because `usePathname()` is
  // stable across re-renders of the same route — the subtree under
  // `<Providers>` does not remount when the user navigates within
  // the non-`/debug` half of the app.
  const pathname = usePathname();
  if (pathname === '/debug') return <>{children}</>;
  return (
    <CoreProviders>
      <UIShellProviders>{children}</UIShellProviders>
    </CoreProviders>
  );
}
